import { useState, useEffect, useRef } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { usePortalAuth } from "@/context/PortalAuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useT, useLang, LANGUAGES } from "@/i18n";
import { LayoutDashboard, Wifi, CreditCard, MessageCircle, UserCircle, LogOut, Menu, X, Sun, Moon, ChevronDown } from "lucide-react";
import portalApi from "@/lib/portalApi";
import { playNotificationSound } from "@/components/shared/ChatComponents";
import "@/styles/crm.css";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_d216ca66-0733-41eb-aefb-a68564ba10f0/artifacts/fn20k37z_FINAL%20Logo%20Newsky%20png.png";

function PortalLangSelector() {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all"
        style={{ background: "var(--crm-surface)", border: "1px solid var(--crm-border)", color: "var(--crm-text-muted)" }}>
        <span className="text-sm">{current.flag}</span>
        <span className="hidden sm:inline">{current.label}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-40 rounded-xl overflow-hidden z-50"
          style={{ background: "var(--crm-surface)", border: "1px solid var(--crm-border)", boxShadow: "var(--crm-shadow-lg)" }}>
          {LANGUAGES.map((l) => (
            <button key={l.code} onClick={() => { setLang(l.code); setOpen(false); }}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 text-xs transition-colors"
              style={{ background: lang === l.code ? "var(--crm-accent-subtle)" : "transparent", color: lang === l.code ? "var(--crm-accent)" : "var(--crm-text-muted)", borderBottom: "1px solid var(--crm-border)" }}>
              <span className="text-sm">{l.flag}</span><span className="font-medium">{l.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PortalLayout() {
  const { user, logout } = usePortalAuth();
  const { theme, toggle } = useTheme();
  const t = useT();
  const p = t.portal;
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileNav, setMobileNav] = useState(false);
  const [unreadPopup, setUnreadPopup] = useState(null);
  const [popupDismissed, setPopupDismissed] = useState(false);

  const navItems = [
    { label: p.nav.home, icon: LayoutDashboard, path: "/portal" },
    { label: p.nav.services, icon: Wifi, path: "/portal/services" },
    { label: p.nav.payments, icon: CreditCard, path: "/portal/payments" },
    { label: p.nav.support, icon: MessageCircle, path: "/portal/support" },
    { label: p.nav.account, icon: UserCircle, path: "/portal/account" },
  ];

  const isActive = (path) => path === "/portal" ? location.pathname === "/portal" : location.pathname.startsWith(path);
  const isOnSupportPage = location.pathname === "/portal/support";

  useEffect(() => {
    if (!user || isOnSupportPage) return;
    const checkUnread = () => {
      portalApi.get("/portal/chat").then((r) => {
        const msgs = r.data || [];
        const lastMsg = msgs[msgs.length - 1];
        if (lastMsg && lastMsg.sender === "admin" && !popupDismissed) {
          setUnreadPopup(lastMsg);
          playNotificationSound();
        }
      }).catch(() => {});
    };
    checkUnread();
    const iv = setInterval(checkUnread, 10000);
    return () => clearInterval(iv);
  }, [user, isOnSupportPage, popupDismissed]);

  useEffect(() => {
    if (isOnSupportPage) { setPopupDismissed(true); setUnreadPopup(null); }
  }, [isOnSupportPage]);

  const handleGoToSupport = () => { setUnreadPopup(null); setPopupDismissed(true); navigate("/portal/support"); };

  return (
    <div className="crm-page">
      {unreadPopup && !isOnSupportPage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className="crm-card w-full max-w-md overflow-hidden" style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.3)", border: "2px solid var(--crm-accent)" }}>
            <div className="px-6 py-4 flex items-center gap-3" style={{ background: "var(--crm-accent)", color: "white" }}>
              <MessageCircle className="w-6 h-6" />
              <div><p className="text-base font-bold">{p.supportTitle}</p></div>
            </div>
            <div className="px-6 py-5">
              <div className="px-4 py-3 rounded-xl text-sm" style={{ background: "var(--crm-input-bg)", border: "1px solid var(--crm-border)", color: "var(--crm-text)" }}>{unreadPopup.message}</div>
              <p className="text-[11px] mt-2 px-1" style={{ color: "var(--crm-text-dim)" }}>{new Date(unreadPopup.created_at).toLocaleString("pt-BR")}</p>
            </div>
            <div className="px-6 pb-5 flex gap-3">
              <button onClick={handleGoToSupport} className="crm-btn-primary flex-1"><MessageCircle className="w-4 h-4" /> {p.nav.support}</button>
              <button onClick={() => { setUnreadPopup(null); setPopupDismissed(true); }} className="crm-btn-secondary">{p.cancelBtn || "OK"}</button>
            </div>
          </div>
        </div>
      )}

      <header className="sticky top-0 z-40 backdrop-blur-md" style={{ background: "var(--crm-topbar-bg)", borderBottom: "1px solid var(--crm-border)" }}>
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => setMobileNav(!mobileNav)} className="sm:hidden p-1.5" style={{ color: "var(--crm-text-muted)" }}>
              {mobileNav ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <Link to="/portal" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="NewSky" className="h-8 w-auto" />
              <span className="text-sm font-bold hidden sm:inline" style={{ color: "var(--crm-text)" }}>{p.portalLabel}</span>
            </Link>
          </div>

          <nav className="hidden sm:flex items-center gap-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link key={item.path} to={item.path}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap"
                  style={{ background: active ? "var(--crm-accent-subtle)" : "transparent", color: active ? "var(--crm-accent)" : "var(--crm-text-muted)" }}>
                  <Icon className="w-4 h-4" strokeWidth={1.5} />{item.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            <PortalLangSelector />
            <button onClick={toggle} className="p-2 rounded-lg" style={{ color: "var(--crm-text-muted)", background: "var(--crm-surface)" }}>
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <button onClick={logout} className="flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg transition-colors" style={{ color: "var(--crm-text-dim)" }}>
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">{p.nav.logout}</span>
            </button>
          </div>
        </div>

        {mobileNav && (
          <nav className="sm:hidden px-4 pb-3 flex flex-wrap gap-2" style={{ borderTop: "1px solid var(--crm-border)" }}>
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link key={item.path} to={item.path} onClick={() => setMobileNav(false)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium"
                  style={{ background: active ? "var(--crm-accent-subtle)" : "var(--crm-surface)", color: active ? "var(--crm-accent)" : "var(--crm-text-muted)" }}>
                  <Icon className="w-4 h-4" strokeWidth={1.5} /> {item.label}
                </Link>
              );
            })}
          </nav>
        )}
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 sm:py-8"><Outlet /></main>
    </div>
  );
}
